import React, { useState } from "react";
import styled from "styled-components";
import { Body2 } from "app/popleads/components/ui-kit/Text";
import { colors } from "app/popleads/components/ui-kit/colors";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import moment from "moment";
import { FormattedNumber } from "react-intl";
import useRemoveDealPayment from "app/popleads/hooks/useRemoveDealPayment";
import Modal from "app/popleads/components/ui-kit/Modal";
import { useExtensionContext } from "../ExtensionContext";

const StyledHorizontalLine = styled("div")`
    border-bottom: 1px solid ${colors.neutral[100]};
    margin-top: 12px;
    margin-bottom: 12px;
`;

const StyledPaidContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 16px;
`;

const StyledDisabledText = styled(Body2)`
    color: ${colors.neutral[600]};
`;

const StyledAmountInfo = styled("div")`
    display: flex;
    align-items: center;
    margin-bottom: ${({ isLast }) => (isLast ? "0" : "12px")};
    gap: 12px;
    justify-content: space-between;
`;

const StyledAmountInfoContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const StyledPaidDate = styled(Body2)`
    color: ${colors.neutral[800]};
    max-width: 100%;
`;

const StyledAmount = styled(Body2)`
    color: ${colors.success[900]};
    font-weight: 500;
    max-width: 100%;
    font-size: 14px;
`;

const ExtensionPaymentList = ({ payments, getDealPayments }) => {
    const { removeDealPayment, loading: deleteLoading } = useRemoveDealPayment();
    const { setState } = useExtensionContext();
    const [isDeletePaidModal, setIsDeletePaidModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const handleDeletePayment = (payment) => {
        setSelectedPayment(payment);
        setIsDeletePaidModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await removeDealPayment(selectedPayment.id);
            setIsDeletePaidModal(false);
            await getDealPayments();
            setSelectedPayment(null);
            setState((prevState) => ({
                ...prevState,
                dealPaymentAdded: true,
            }));
        } catch (error) {
            // Error is already handled in the hook
        } finally {
            setIsDeletePaidModal(false);
        }
    };

    if (!payments?.length) return null;

    return (
        <>
            <StyledHorizontalLine />
            <StyledPaidContainer>
                <StyledDisabledText>Paid Amount</StyledDisabledText>
                <div>
                    {payments.map((paidAmount, index) => (
                        <StyledAmountInfo isLast={index + 1 === payments.length} key={index}>
                            <StyledPaidDate>
                                {moment(paidAmount.createdDate).format("MMM DD, YYYY HH:mm")}
                            </StyledPaidDate>
                            <StyledAmountInfoContainer>
                                <StyledAmount>
                                    ${" "}
                                    <FormattedNumber
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={2}
                                        value={paidAmount.paidAmount}
                                    />
                                </StyledAmount>
                                <ButtonWithText
                                    onClick={() => handleDeletePayment(paidAmount)}
                                    icon="x"
                                    iconType="feather"
                                    variant="danger"
                                    size="circle"
                                    buttonStyle="borderless"
                                />
                            </StyledAmountInfoContainer>
                        </StyledAmountInfo>
                    ))}
                </div>
            </StyledPaidContainer>

            <Modal
                isOpen={isDeletePaidModal}
                onClose={() => setIsDeletePaidModal(false)}
                title="Are you sure you want to remove this payment?"
                variant="danger"
                icon="warning"
                successButtonText={deleteLoading ? "Deleting..." : "Yes, Delete"}
                cancelButtonText="Not Now"
                onSuccess={handleConfirmDelete}
                onCancel={() => {
                    setIsDeletePaidModal(false);
                    setSelectedPayment(null);
                }}
            />
        </>
    );
};

export default ExtensionPaymentList;
